/**
 * EdgeStyleFields — the stroke controls for a map path, as a controlled field set.
 *
 * Shared by the two places a path look is authored: the per-path PathEditModal
 * (one edge's override) and the Map Settings modal's Strokes tab (the map-wide
 * default every un-styled edge inherits). Keeping one component means the two
 * surfaces can never drift apart.
 *
 * Pure presentation — it owns no state and never saves; the parent holds the
 * value and decides what persisting it means. Mirrors the
 * StalemateConfigFields / RadarConfigFields convention used by the Game
 * Settings tabs.
 */

import {
  DEFAULT_TRL_EDGE_STYLE,
  TRL_AUTHORABLE_EDGE_PATTERNS,
  edgeStyleToSvg,
  type TrlEdgeStyle,
  type TrlEdgeLinePattern,
} from '@swtorbit/shared-socket-events';
import { Segmented } from '@swtorbit/ui/react';

interface EdgeStyleFieldsProps {
  /** The style being edited. Absent fields fall back to the built-in default. */
  value: TrlEdgeStyle;
  onChange: (next: TrlEdgeStyle) => void;
}

/**
 * Built from the shared authorable list so the picker can never drift from it.
 * Solid is not offered: the player map uses it for a team's travelled route.
 */
const PATTERN_LABELS: Record<TrlEdgeLinePattern, string> = {
  solid: 'Solid',
  dashed: 'Dashed',
  dotted: 'Dotted',
};
const PATTERNS = TRL_AUTHORABLE_EDGE_PATTERNS.map((value) => ({
  value,
  label: PATTERN_LABELS[value],
}));

/** Thickness bounds, matched by both the input attrs and the clamp on change. */
const MIN_WIDTH = 0.5;
const MAX_WIDTH = 8;

export function EdgeStyleFields({ value, onChange }: EdgeStyleFieldsProps) {
  const color = value.color ?? DEFAULT_TRL_EDGE_STYLE.color;
  const width = value.width ?? DEFAULT_TRL_EDGE_STYLE.width;
  // A map authored before solid was reserved can still carry it; show the
  // default instead so the segmented control always has a selected option.
  const storedPattern = value.pattern ?? DEFAULT_TRL_EDGE_STYLE.pattern;
  const pattern = TRL_AUTHORABLE_EDGE_PATTERNS.includes(storedPattern)
    ? storedPattern
    : DEFAULT_TRL_EDGE_STYLE.pattern;
  const rounded = value.rounded ?? DEFAULT_TRL_EDGE_STYLE.rounded;

  const previewSvg = edgeStyleToSvg({ color, width, pattern, rounded });

  const patch = (next: Partial<TrlEdgeStyle>) =>
    onChange({ color, width, pattern, rounded, ...next });

  return (
    <div className="d-flex flex-column gap-sm">
      {/* Live preview */}
      <svg
        width="100%"
        height="32"
        viewBox="0 0 300 32"
        preserveAspectRatio="none"
        role="img"
        aria-label="Path preview"
      >
        <line
          x1="6"
          y1="16"
          x2="294"
          y2="16"
          fill="none"
          stroke={previewSvg.stroke}
          strokeWidth={previewSvg.strokeWidth}
          strokeDasharray={previewSvg.strokeDasharray}
          strokeLinecap={previewSvg.strokeLinecap}
          strokeLinejoin={previewSvg.strokeLinejoin}
        />
      </svg>

      {/* Pattern */}
      <div className="d-flex flex-column gap-2xs fs-sm">
        <span>Line pattern</span>
        <Segmented
          size="sm"
          aria-label="Line pattern"
          options={PATTERNS}
          value={pattern}
          onChange={(value) => patch({ pattern: value })}
        />
        <span className="fs-xs text-slate">
          Solid is reserved for the route each team has travelled on the player map.
        </span>
      </div>

      {/* Thickness */}
      <label className="d-flex align-items-center gap-xs fs-sm">
        <span>Thickness</span>
        <input
          type="number"
          className="input"
          style={{ width: '6rem' }}
          min={MIN_WIDTH}
          max={MAX_WIDTH}
          step={0.5}
          value={width}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!Number.isNaN(v)) patch({ width: Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, v)) });
          }}
        />
        <span className="fs-xs text-slate">px</span>
      </label>

      {/* Color */}
      <label className="d-flex align-items-center gap-xs fs-sm">
        <span>Color</span>
        <input
          type="color"
          className="input"
          style={{ width: '4rem', padding: 0 }}
          value={toHexColor(color)}
          onChange={(e) => patch({ color: e.target.value })}
        />
      </label>

      {/* Rounded caps */}
      <label className="d-flex align-items-center gap-2xs fs-sm">
        <input
          className="checkbox"
          type="checkbox"
          checked={rounded}
          onChange={(e) => patch({ rounded: e.target.checked })}
        />
        <span>Rounded caps &amp; corners</span>
      </label>
    </div>
  );
}

/**
 * `<input type="color">` only accepts `#rrggbb`. Authored colors may be rgba()
 * (the default look) or other CSS forms — fall back to a sensible opaque hex so
 * the swatch stays usable; picking a new value overwrites with a clean hex.
 */
function toHexColor(color: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#ffffff';
}
